import React from 'react';
import {useLocation, useMatch} from 'react-router-dom';
import {PATHS} from 'Routes';
import {environments} from '../configs';
import {APP_FLOWS, APP_PAGES} from 'utils/pages';
import {usePath} from 'hooks';
import {useSummary} from 'context/summary';
import {useSchema} from 'context/guest';

const IS_SDK_MODE = environments.VITE_APP_IS_SDK;

function useNavigationCustom() {
  const location = useLocation();
  const {getMainPath} = usePath();
  const {isGuestRegistrationOnly} = useSummary();
  const {
    identityVerificationDetails: {enabled: ivEnabled},
  } = useSchema();

  const rootPath = IS_SDK_MODE ? '' : '/:token';

  const isHomeView = useMatch(rootPath || '/');
  const isGuestRegistrationView = useMatch(getMainPath(PATHS.guestRegistration));
  const isVerificationView = useMatch(`${rootPath}/verification`);
  const isIdentityVerificationView = location.pathname.includes(PATHS.verification.main);
  const isTaxesView = useMatch(`${rootPath}/taxes`);
  const isTaxesExemptionsView = useMatch(`${rootPath}/taxes/exemptions`);
  const isPaymentsFormsViews = useMatch(`${rootPath}/payments/form`);
  const isPaymentsView =
    location.pathname.includes(PATHS.payments.main) && !isPaymentsFormsViews;
  const isDepositView = useMatch(getMainPath(PATHS.deposits));
  const isRemoteAccessView = useMatch(getMainPath(PATHS.remoteAccess));
  const isAddGuestFormView = useMatch(getMainPath(`${PATHS.addPersonalDataForm}/:id?`));
  const isSummaryView = useMatch(getMainPath(PATHS.summary));
  const isAutoFillView = useMatch(getMainPath(PATHS.documentMRZ));
  const isBookExperience = useMatch(`${rootPath}/recommendations/:id/book-experience`);
  const isRecommendationsConfirmEmail = useMatch(
    `${rootPath}/recommendations/:id/confirm-email`,
  );
  const isCheckoutView = useMatch(getMainPath(PATHS.checkout.main));

  const isFAQFormView = useMatch(getMainPath(PATHS.faq));

  const isAutoFillUploadView =
    !isAutoFillView && location.pathname.includes(PATHS.documentMRZ);

  const currentPage = React.useMemo(() => {
    if (isGuestRegistrationView) {
      return APP_PAGES.registerGuest;
    }
    if (isVerificationView) {
      return APP_PAGES.verification;
    }
    if (isIdentityVerificationView) {
      return APP_PAGES.verificationChild;
    }
    if (isTaxesView) {
      return location.state?.from
        ? `${APP_PAGES.taxes}_${location.state?.from}`
        : APP_PAGES.taxes;
    }
    if (isTaxesExemptionsView) {
      return APP_PAGES.taxesAddExemption;
    }
    if (isPaymentsFormsViews) {
      return location.state?.from
        ? `${APP_PAGES.paymentsForm}_${location.state?.from}`
        : APP_PAGES.paymentsForm;
    }
    if (isPaymentsView) {
      return location.state?.from
        ? `${APP_PAGES.payments}_${location.state?.from}`
        : APP_PAGES.payments;
    }
    if (isDepositView) {
      return APP_PAGES.deposits;
    }
    if (isAddGuestFormView) {
      return APP_PAGES.addPersonalData;
    }
    if (isSummaryView) {
      return APP_PAGES.summary;
    }
    if (isAutoFillView) {
      return APP_PAGES.autoFill;
    }
    if (isAutoFillUploadView) {
      return APP_PAGES.autoFillUpload;
    }
    if (isHomeView) {
      return APP_PAGES.home;
    }
    if (isRemoteAccessView) {
      return APP_PAGES.remoteAccess;
    }
    if (isFAQFormView) {
      return APP_PAGES.faq;
    }
    if (isBookExperience) {
      return APP_PAGES.bookExperience;
    }
    if (isRecommendationsConfirmEmail) {
      return APP_PAGES.recommendationsConfirmEmail;
    }
    if (isCheckoutView) {
      return APP_PAGES.checkout;
    }
    return APP_PAGES.home;
  }, [
    isGuestRegistrationView,
    isVerificationView,
    isIdentityVerificationView,
    isTaxesView,
    isTaxesExemptionsView,
    isPaymentsFormsViews,
    isPaymentsView,
    isDepositView,
    isAddGuestFormView,
    isSummaryView,
    isAutoFillView,
    isAutoFillUploadView,
    isHomeView,
    isRemoteAccessView,
    isFAQFormView,
    isBookExperience,
    isRecommendationsConfirmEmail,
    isCheckoutView,
    location.state?.from,
  ]);

  const currentFlow = React.useMemo(() => {
    if (!isGuestRegistrationOnly) {
      return APP_FLOWS.allFeatures;
    }

    return ivEnabled ? APP_FLOWS.grIvOnly : APP_FLOWS.grOnly;
  }, [isGuestRegistrationOnly, ivEnabled]);

  const currentFlowAndPage = React.useMemo(() => {
    return `${currentFlow}_${currentPage}`;
  }, [currentFlow, currentPage]);

  return {currentPage, currentFlowAndPage, isPaymentsView, currentFlow};
}

export {useNavigationCustom};
